import * as z from "zod";
import {
  anyOfFilters,
  authProviders,
  code,
  cursorPagination,
  languageTag,
  pagination,
  uuidV7,
  validationIssue,
  version,
} from "../index";
import { orgTypes, roleAudiences, userStatuses } from "./enums";
import { emailAddress as email, organizationRef, shortText } from "./fields";

// Users and the roles they hold (FR-M1-01…04; 50-api/party.md §2.1). Credentials are never here: an external user
// gets them from identity (ADR-024), an internal one signs in with Logto.

const namePart = shortText(100);

/**
 * Whose queue an invitation not accepted after its last reminder is in (B-44, B-88, ADR-245): Customer Service's for
 * a user of a customer, Operations' for one of a brand or a supplier; an organization that is both is in both.
 */
export const invitationQueues = ["customer", "brand_or_supplier"] as const;
export type InvitationQueue = (typeof invitationQueues)[number];

const userFilters = {
  /** Part of the name or of the email, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  orgId: z.uuid().optional(),
  orgType: z.enum(orgTypes).optional(),
  status: z.enum(userStatuses).optional(),
  /** Code of a role the user holds. */
  role: code.optional(),
  /**
   * External users still `invited` a cadence after the last reminder of their invitation, of a customer or of a brand
   * or supplier: the queue of who sent it (B-88, ADR-245). Only Tomorrow filters by it.
   */
  invitationRemindersOver: z.enum(invitationQueues).optional(),
};

/** The users in at least one of these sets of the filters (ADR-302): the queues of the invitations, to do. */
const anyOfUsers = { anyOf: anyOfFilters(userFilters).optional() };

/** By last name, then first name and email. */
export const listUsersInput = z.object({
  ...userFilters,
  ...anyOfUsers,
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListUsersInput = z.input<typeof listUsersInput>;

/** How many users the list has with the same filters, and since when the oldest is untouched (ADR-086). */
export const countUsersInput = z.object({ ...userFilters, ...anyOfUsers });
export type CountUsersInput = z.input<typeof countUsersInput>;

export const getUserInput = z.object({ userId: z.uuid() });
export type GetUserInput = z.input<typeof getUserInput>;

export const getUserOptionsInput = z.object({});
export type GetUserOptionsInput = z.input<typeof getUserOptionsInput>;

/** Newest first. An id nobody has answers an empty history. */
export const getUserHistoryInput = z.object({
  userId: z.uuid(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 100 }),
});
export type GetUserHistoryInput = z.input<typeof getUserHistoryInput>;
export const userHistoryQuery = getUserHistoryInput.omit({ userId: true });

/**
 * The id comes from the client (ADR-046): a retry by the same user returns the user already created. The user is
 * born `invited` and without roles; how they sign in follows from the organization (Logto for Tomorrow's, the
 * credentials of Nexxt for the others).
 */
export const createUserInput = z.object({
  id: uuidV7,
  orgId: z.uuid(),
  email,
  firstName: namePart,
  lastName: namePart,
  locale: languageTag.optional(),
});
export type CreateUserInput = z.input<typeof createUserInput>;

/** PATCH: an absent field stays as it is. The organization of a user never changes. */
export const updateUserInput = z.object({
  userId: z.uuid(),
  version,
  email: email.optional(),
  firstName: namePart.optional(),
  lastName: namePart.optional(),
  locale: languageTag.optional(),
});
export type UpdateUserInput = z.input<typeof updateUserInput>;

/**
 * The languages a user can choose for its emails and, one day, its app (Q-PAR-04 is open): the ones the templates are
 * written in. A language is added here when its templates and its messages are.
 */
export const profileLocales = ["en"] as const;
export type ProfileLocale = (typeof profileLocales)[number];

/**
 * The signed-in user's own name and language (ADR-163): PATCH, an absent field stays. The user is the Actor of the
 * session, never the input (ADR-148); the email has its own road, with a link to the new address.
 */
export const updateOwnProfileInput = z.object({
  firstName: namePart.optional(),
  lastName: namePart.optional(),
  locale: z.enum(profileLocales).optional(),
});
export type UpdateOwnProfileInput = z.input<typeof updateOwnProfileInput>;

export const ownProfileDto = z
  .object({ firstName: z.string().nullable(), lastName: z.string().nullable(), locale: z.string() })
  .meta({ id: "OwnProfile" });

/** A role of a user: with `scopeOrgId` it also covers that organization (an agency on several brands, Q-01). */
const roleGrant = z.object({ role: code, scopeOrgId: z.uuid().nullable() });

/** The whole set: a role left out is taken away. */
export const setUserRolesInput = z.object({
  userId: z.uuid(),
  version,
  roles: z.array(roleGrant).max(50),
});
export type SetUserRolesInput = z.input<typeof setUserRolesInput>;

/** An action on a user, with the version the client read. */
export const userActionInput = z.object({ userId: z.uuid(), version });
export type UserActionInput = z.input<typeof userActionInput>;

/** The gateway routes split the input: the id in the path, the rest in the body. */
export const userParams = userActionInput.pick({ userId: true });
export const userVersionBody = userActionInput.pick({ version: true });
export const updateUserBody = updateUserInput.omit({ userId: true });
export const setUserRolesBody = setUserRolesInput.omit({ userId: true });

export const CHANGE_USERS_ROLES_MAX = 100;

/**
 * Roles given to and taken from several users at once (ADR-305): a change, not a set, so it holds whatever else a
 * user holds and asks no version. A role given is given for the user's own organization; a role taken is taken with
 * every organization it also covered.
 */
export const changeUsersRolesInput = z
  .object({
    userIds: z
      .array(z.uuid())
      .min(1)
      .max(CHANGE_USERS_ROLES_MAX)
      .refine((ids) => new Set(ids).size === ids.length, { message: "a user is named more than once" }),
    add: z.array(code).max(50),
    remove: z.array(code).max(50),
  })
  .refine((change) => change.add.length + change.remove.length > 0, { message: "nothing to change" })
  .refine((change) => !change.add.some((role) => change.remove.includes(role)), {
    message: "a role is given and taken",
  });
export type ChangeUsersRolesInput = z.input<typeof changeUsersRolesInput>;

export const userDto = z
  .object({
    id: z.uuid(),
    email: z.string(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    locale: z.string().nullable().optional(),
    status: z.enum(userStatuses),
    authProvider: z.enum(authProviders),
    organization: organizationRef.extend({ type: z.enum(orgTypes) }),
    roles: z.array(z.object({ code: z.string(), name: z.string(), scopeOrganization: organizationRef.nullable() })),
    lastLoginAt: z.iso.datetime().nullable().optional(),
    createdAt: z.iso.datetime(),
    version: z.number().int(),
  })
  .meta({ id: "User" });

/**
 * What the forms of a user offer (ADR-048): every role, with who it is for. To an organization's own administrator,
 * only the roles its organization may give (ADR-172).
 */
export const userOptionsDto = z.object({
  roles: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      audience: z.enum(roleAudiences),
      /** What the role is for, in a line (ADR-305); null until Tomorrow writes it. */
      description: z.string().nullable(),
    }),
  ),
});

/**
 * What a change of roles did to each user, in the order asked (ADR-305). `issues` name the role in `value`, since the
 * set they were checked on is the domain's, not the client's.
 */
export const changeUsersRolesDto = z
  .object({
    users: z.array(
      z.object({
        userId: z.uuid(),
        outcome: z.enum(["changed", "unchanged", "refused"]),
        /** The version of the user after the change; null when refused. */
        version: z.number().int().nullable(),
        /** Why the user was not changed: the code and the issues of the same refusal as one change of roles. */
        error: z.object({ code: z.string(), issues: z.array(validationIssue) }).nullable(),
      }),
    ),
  })
  .meta({ id: "ChangeUsersRolesResult" });
export type ChangeUsersRolesResult = z.output<typeof changeUsersRolesDto>;
